import { Form, Input, message, Select } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { MakeApiCall } from "../../../apis";
import { DefaultAdminPage, DefaultUserPage, Types } from "../../../config";
import { setData } from "../../../redux/actions";


/* ── Country codes ── */
const COUNTRY_CODES = [
  { label: "🇮🇳 +91",  value: "+91"  },
  { label: "🇺🇸 +1",   value: "+1"   },
  { label: "🇬🇧 +44",  value: "+44"  },
  { label: "🇦🇺 +61",  value: "+61"  },
  { label: "🇦🇪 +971", value: "+971" },
  { label: "🇸🇬 +65",  value: "+65"  },
  { label: "🇲🇾 +60",  value: "+60"  },
];

/* ── Route map ── */
const findRoutes = {
  superAdmin: DefaultAdminPage,
  admin:      DefaultAdminPage,
  manager:    DefaultAdminPage,
  user:       DefaultUserPage,
};

/* ── Animations ── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background: #263d57;
//   font-family: 'DM Sans', 'Segoe UI', sans-serif;
  overflow: hidden;

  /* ── ANT overrides ── */
  .ant-input-affix-wrapper,
  .ant-input {
    background: #fafafa !important;
    border: 1.5px solid #e8e8ea !important;
    border-radius: 10px !important;
    font-size: 13.5px !important;
    height: 46px !important;
    padding: 0 14px !important;
    color: #1D1E20 !important;
    box-shadow: none !important;
    transition: border-color 0.15s !important;
  }
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  .ant-input:focus {
    border-color: #F35B68 !important;
    background: #fff !important;
  }
  .ant-input::placeholder { color: #ccc !important; }
  .ant-input-affix-wrapper .ant-input {
    height: 100% !important;
    border: none !important;
    background: transparent !important;
  }
  .ant-form-item { margin-bottom: 12px !important; }
  .ant-form-item-explain-error {
    font-size: 12px !important;
    margin-top: 4px !important;
    color: #f07070 !important;
  }
`;

/* ── LEFT panel ── */
const Left = styled.div`
  width: 520px;
  min-width: 420px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 52px 48px;
  box-shadow: 12px 0 48px rgba(0,0,0,0.30);
  position: relative;
  z-index: 2;
  animation: ${fadeUp} 0.6s ease both;

  @media (max-width: 900px) {
    width: 100%;
    min-width: unset;
    box-shadow: none;
    padding: 44px 32px;
  }
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: #1D1E20;
  margin-bottom: 40px;
  img { height: 50px; object-fit: contain;margin-left: -15px; }
`;

const Heading = styled.h1`
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.6px;
  color: #1D1E20;
  line-height: 1.25;
  margin: 0 0 6px;
`;

const SubText = styled.p`
  font-size: 13px;
  color: #535353;
  font-weight: 300;
  margin: 0 0 28px;
`;

const Label = styled.label`
  display: block;
  font-size: 10.5px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 5px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 13px;
  background: #F35B68;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
  transition: background 0.15s, transform 0.1s;
  letter-spacing: -0.1px;

  &:hover  { background: #d94757; }
  &:active { transform: scale(0.99); }

  .icon {
    width: 22px; height: 22px;
    background: rgba(255,255,255,0.2);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px;
  }
`;

const ForgotLink = styled.div`
  text-align: right;
  margin: 4px 0 14px;
  a {
    font-size: 12.5px;
    color: #F35B68;
    text-decoration: none;
    font-weight: 500;
  }
`;

const SignupLink = styled.div`
  text-align: center;
  font-size: 12.5px;
  color: #535353;
  margin-top: 18px;
  a { color: #F35B68; text-decoration: none; font-weight: 500; }
`;

const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid #f0f0ee;
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #535353;
  font-weight: 300;
  &::before {
    content: '';
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #355071;
    opacity: 0.5;
    display: inline-block;
  }
`;

/* ── RIGHT panel ── */
const Right = styled.div`
  flex: 1;
  background: #355071;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 64px;
  position: relative;
  overflow: hidden;

  @media (max-width: 900px) { display: none; }
`;

const Glow = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(80px);
  opacity: 0.18;
  &.g1 { width: 360px; height: 360px; background: #F35B68; top: -80px; right: -80px; }
  &.g2 { width: 260px; height: 260px; background: #fff; bottom: -60px; left: -60px; opacity: 0.04; }
`;

const Ring = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  border: 0.5px solid rgba(255,255,255,0.06);
  &.r1 { width: 640px; height: 640px; top: -260px; right: -220px; }
  &.r2 { width: 420px; height: 420px; top: -140px; right: -100px; border-color: rgba(243,91,104,0.12); }
  &.r3 { width: 520px; height: 520px; bottom: -320px; left: -220px; }
`;

const RightInner = styled.div`
  max-width: 520px;
  width: 100%;
  position: relative;
  z-index: 1;
  animation: ${fadeUp} 0.7s ease 0.2s both;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 600;
  color: #f9868f;
  text-transform: uppercase;
  letter-spacing: 1.6px;
  margin-bottom: 16px;
  span { width: 20px; height: 1.5px; background: #F35B68; opacity: 0.5; border-radius: 1px; display: inline-block; }
`;

const BigNum = styled.div`
  display: flex;
  align-items: flex-start;
  line-height: 1;
  margin-bottom: 10px;
  .num {
    font-size: 96px;
    font-weight: 700;
    color: #fff;
    letter-spacing: -6px;
    line-height: 0.88;
  }
  .pct {
    font-size: 44px;
    font-weight: 600;
    color: #F35B68;
    margin-top: 8px;
    letter-spacing: -2px;
  }
`;

const StatBody = styled.p`
  font-size: 15px;
  color: rgba(255,255,255,0.55);
  font-weight: 300;
  line-height: 1.65;
  max-width: 400px;
  margin: 0 0 28px;
  strong { color: rgba(255,255,255,0.88); font-weight: 500; }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;
  .line { flex: 1; height: 0.5px; background: rgba(255,255,255,0.1); }
  .label {
    font-size: 10px;
    font-weight: 600;
    color: rgba(255,255,255,0.2);
    text-transform: uppercase;
    letter-spacing: 1.2px;
    white-space: nowrap;
  }
`;

const WinBlock = styled.div`
  margin-bottom: 28px;
  .headline {
    font-size: 20px;
    font-weight: 600;
    font-style: italic;
    color: #fff;
    line-height: 1.4;
    letter-spacing: -0.4px;
    margin-bottom: 8px;
    em { color: #F35B68; font-style: normal; }
  }
  .body {
    font-size: 13px;
    color: rgba(255,255,255,0.45);
    font-weight: 300;
    line-height: 1.7;
    strong { color: rgba(255,255,255,0.7); font-weight: 400; }
  }
`;

const UECard = styled.div`
  background: rgba(255,255,255,0.05);
  border: 0.5px solid rgba(255,255,255,0.1);
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 20px;

  .ue-label {
    font-size: 10px;
    font-weight: 600;
    color: rgba(255,255,255,0.4);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    &::after { content: ''; flex: 1; height: 0.5px; background: rgba(255,255,255,0.07); }
  }

  .metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: 18px;
  }

  .metric {
    padding-right: 16px;
    border-right: 0.5px solid rgba(255,255,255,0.07);
    &:last-child  { border-right: none; padding-right: 0; padding-left: 16px; }
    &:nth-child(2){ padding: 0 16px; }

    .m-label { font-size: 10px; color: rgba(255,255,255,0.25); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px; }
    .m-val   { font-size: 17px; font-weight: 600; letter-spacing: -0.5px; line-height: 1; margin-bottom: 3px; }
    .m-sub   { font-size: 10px; font-weight: 300; }
    &.white  .m-val { color: rgba(255,255,255,0.88); }
    &.red    .m-val, &.red  .m-sub { color: #f07070; }
    &.green  .m-val, &.green .m-sub{ color: #6dbb3a; }
  }

  .bar-track {
    display: flex; gap: 3px; height: 6px; border-radius: 3px; overflow: hidden; margin-bottom: 8px;
  }
  .bar-seg { height: 100%; border-radius: 2px; }

  .legend {
    display: flex; gap: 14px; flex-wrap: wrap;
    span {
      display: flex; align-items: center; gap: 4px;
      font-size: 9.5px; color: rgba(255,255,255,0.3);
      i { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
    }
  }
`;

const Testimonial = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  .avatar {
    width: 30px; height: 30px; border-radius: 50%;
    background: #F35B68;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700; color: #fff;
    flex-shrink: 0; margin-top: 1px;
  }
  .quote { font-size: 12px; color: rgba(255,255,255,0.45); font-style: italic; line-height: 1.65; font-weight: 300; margin-bottom: 5px; em { color: rgba(255,255,255,0.8); font-style: normal; font-weight: 400; } }
  .by    { font-size: 10px; color: rgba(255,255,255,0.35); font-weight: 500; }
`;

const PhoneRow = styled.div`
  display: flex;
  gap: 8px;
  .ant-select { width: 105px !important; flex-shrink: 0;height: 46px;font-size: 14px; }
  .ant-select-focused .ant-select-selector {
    border-color: #F35B68 !important;
    box-shadow: none !important;
    background: #fff !important;
  }
.ant-select-outlined:not(.ant-select-disabled):not(.ant-select-customize-input):not(.ant-pagination-size-changer):hover .ant-select-selector {
    border-color: #F35B68 !important;
}
  .phone-input { flex: 1; }

`;

const SuccessBanner = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;

  .icon { font-size: 16px; margin-top: 1px; flex-shrink: 0; }
  .text { font-size: 13px; color: #166534; font-weight: 400; line-height: 1.5; }
`;

/* ════════════════════════════════════
   COMPONENT
════════════════════════════════════ */
export default function SignUp() {
  const [form]  = Form.useForm();
  const dispatch = useDispatch();
  const user     = useSelector((state) => state.user.data);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const verificationToken = searchParams.get("token");
  const hasToken = !!verificationToken;

  useEffect(() => {
    if (Object.keys(user)?.length !== 0) {
      navigate(findRoutes?.[user.loginType]);
    }
  }, []);

  const handleLogin = (data) => {
    const type = Types?.[data?.data?.user_data?.user_type];
    dispatch(setData({ user: data?.data, loginType: type, token: data?.data?.auth_token_data?.access_token }));
    // expose logged-in user's id on window for integrations (Pendo, analytics)
    const visitorId = data?.data?.user_data?.id ?? data?.data?.id ?? null;
    if (visitorId) window.visitor_id = visitorId;
    navigate(findRoutes?.[type]);
  };

  const onFinish = async (values) => {
    setSubmitLoading(true);
    try {
      if (!hasToken) {
        const contact_no = `${values.country_code}${values.phone_number}`;
        const response = await MakeApiCall("send-email-validation", "post", { email: values.email, contact_no });
        setSubmitLoading(false);
        if (response?.status === true) {
          const msg = response?.message || "Verification email sent! Please check your inbox.";
          message.success(msg);
          setSuccessMessage(msg);
          setTimeout(() => setSuccessMessage(""), 5000);
        } else {
          message.error(response?.message || "Failed to send verification email.");
        }
      } else {
        const response = await MakeApiCall("sign-up", "post", {
          name: values.name,
          password: values.password,
          confirm_password: values.confirm_password,
          verification_token: verificationToken,
          user_type: 2,
        });
        if (response?.status === true) {
          message.success(response?.message || "Account created! You can now sign in.");
          handleLogin(response);
        } else {
          setSubmitLoading(false);
          message.error(response?.message || "Failed to complete sign-up.");
        }
      }
    } catch (error) {
      setSubmitLoading(false);
      message.error(error?.response?.data?.message || error?.message || "An error occurred.");
    }
  };

  return (
    <Wrapper className="h-100">

      {/* ── LEFT: FORM ── */}
      <Left>
      <Logo>
          <img src="assets/media/SellerViewLogo.png" alt="Sellerview" />
        </Logo>

        <Heading>Know your profit.<br></br>Before it's too late.</Heading>
        <SubText>
          {hasToken ? "Free 14-day trial · No credit card · 2-min setup" : "Enter your email & mobile to receive a verification link"}
        </SubText>

        {successMessage && (
          <SuccessBanner>
            <span className="icon">✅</span>
            <span className="text">{successMessage}</span>
          </SuccessBanner>
        )}

        <Form
          form={form}
          name="register"
          initialValues={{ remember: true, country_code: "+91" }}
          onFinish={onFinish}
        >
          {/* ── STEP 2 ONLY: Name ── */}
          {hasToken && (
            <>
              <Label>Your Name</Label>
              <Form.Item name="name" rules={[{ required: true, message: "Please input your name!" }]}>
                <Input placeholder="Your full name" size="large" />
              </Form.Item>
            </>
          )}

          {/* ── STEP 1: Email + Phone ── */}
          {!hasToken && (
            <>
              <Label>Work Email</Label>
              <Form.Item
                name="email"
                rules={[
                  { type: "email", message: "Please enter a valid email!" },
                  { required: true, message: "Please input your email!" },
                ]}
              >
                <Input placeholder="you@yourstore.com" size="large" />
              </Form.Item>

              <Label>Mobile Number</Label>
              <Form.Item style={{ marginBottom: 0 }}>
                <PhoneRow>
                  <Form.Item name="country_code" noStyle rules={[{ required: true }]}>
                    <Select size="large" options={COUNTRY_CODES} popupMatchSelectWidth={false} />
                  </Form.Item>
                  <Form.Item
                    name="phone_number"
                    noStyle
                    rules={[
                      { required: true, message: "Please input your mobile number!" },
                      { pattern: /^\d{9,10}$/, message: "Enter a valid mobile number!" },
                    ]}
                  >
                    <Input className="phone-input" placeholder="Mobile number" size="large" />
                  </Form.Item>
                </PhoneRow>
              </Form.Item>
            </>
          )}

          {/* ── STEP 2 ONLY: Passwords ── */}
          {hasToken && (
            <>
              <Label>Password</Label>
              <Form.Item name="password" rules={[{ required: true, message: "Please input your password!" }]}>
                <Input.Password placeholder="Create a password" size="large" />
              </Form.Item>

              <Label>Confirm Password</Label>
              <Form.Item
                name="confirm_password"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password!" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) return Promise.resolve();
                      return Promise.reject(new Error("Passwords do not match!"));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm your password" size="large" />
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginTop: 8 }}>
            <SubmitBtn type="submit" disabled={submitLoading}>
              {submitLoading
                ? "Please wait…"
                : <>{hasToken ? "Create Account" : "Send Verification Email"} <div className="icon">→</div></>
              }
            </SubmitBtn>
          </Form.Item>
        </Form>

        <SignupLink>
          Already have an account? <Link to="/sign-in">Sign In</Link>
        </SignupLink>

        <TrustRow>
          <TrustItem>No credit card needed</TrustItem>
          <TrustItem>Supports Global Marketplaces</TrustItem>
          <TrustItem>Cancel anytime</TrustItem>
        </TrustRow>
      </Left>

      {/* ── RIGHT: STORY PANEL ── */}
      <Right>
        <Ring className="r1" /><Ring className="r2" /><Ring className="r3" />
        <Glow className="g1" /><Glow className="g2" />

        <RightInner>
          <Eyebrow><span />The hard truth<span /></Eyebrow>

          <BigNum>
            <div className="num">68</div>
            <div className="pct">%</div>
          </BigNum>

          <StatBody>
            of Amazon sellers <strong>shut down within 3 years</strong> — not because of competition,
            but because of P&amp;L they never truly understood.
          </StatBody>

          <Divider>
            <div className="line" />
            <div className="label">The ones who win</div>
            <div className="line" />
          </Divider>

          <WinBlock>
            <div className="headline">It ain't luck. They track their <em className="ms-1"> unit economics</em> from Day 1.</div>
            <div className="body">
              They know which SKUs make money, where fees eat margin, and whether ads build profit or burn it.
              <strong> That's the only difference.</strong>
            </div>
          </WinBlock>

          <UECard>
            <div className="ue-label">What Day 1 tracking looks like</div>
            <div className="metrics">
              <div className="metric white">
                <div className="m-label">Revenue</div>
                <div className="m-val">₹2,14,000</div>
                <div className="m-sub" style={{ color: "rgba(255,255,255,0.2)" }}>per SKU / month</div>
              </div>
              <div className="metric red">
                <div className="m-label">Total costs</div>
                <div className="m-val">−₹1,51,600</div>
                <div className="m-sub">ads · fees · returns</div>
              </div>
              <div className="metric green">
                <div className="m-label">Net profit</div>
                <div className="m-val">₹62,400</div>
                <div className="m-sub">29% margin ✓</div>
              </div>
            </div>

            <div className="bar-track">
              <div className="bar-seg" style={{ width: "29%", background: "#6dbb3a" }} />
              <div className="bar-seg" style={{ width: "18%", background: "#F35B68" }} />
              <div className="bar-seg" style={{ width: "10%", background: "#f07070" }} />
              <div className="bar-seg" style={{ width: "43%", background: "rgba(255,255,255,0.18)" }} />
            </div>

            <div className="legend">
              <span><i style={{ background: "#6dbb3a" }} />Net profit 29%</span>
              <span><i style={{ background: "#F35B68" }} />Ad spend 18%</span>
              <span><i style={{ background: "#f07070" }} />Returns 10%</span>
              <span><i style={{ background: "rgba(255,255,255,0.25)" }} />Fees 43%</span>
            </div>
          </UECard>

          <Testimonial>
            <div className="avatar">AM</div>
            <div>
              <div className="quote">
                "Was scaling for 2 years thinking I was profitable. Sellerview showed me{" "}
                <em>3 SKUs were quietly bleeding ₹22K/month.</em> Fixed it in a week."
              </div>
              <div className="by">Amit M. · Amazon Seller · Home & Kitchen · Delhi</div>
            </div>
          </Testimonial>
        </RightInner>
      </Right>

    </Wrapper>
  );
}